import { computed, inject, Injectable, signal } from '@angular/core';
import {
  IOnDemandBookingResponse,
  IOnDemandConnectionOption,
  IOperatorMapping,
  IPassengerSummary,
  IJourneyTrip,
  ITariffSystemPayment,
  ON_DEMAND_EXTRA_LABELS,
  RESERVATION_DURATION_MINUTES,
  TariffProductGroup,
  IOnDemandPaymentCustomerData,
  ON_DEMAND_PAYMENT_STATE_KEY,
} from '@web/dtos';
import { OnDemandAvailabilityStore } from '../api/on-demand/on-demand-availability-store.service';
import { ReconRouteStore } from '../api/journey-planning/recon/recon-route-store.service';
import {
  calculateTimeDeviationMinutes,
  formatIsoTime,
} from '@web/shared-functions/date';

@Injectable({ providedIn: 'root' })
export class OnDemandSelectionStateService {
  private readonly availabilityStore = inject(OnDemandAvailabilityStore);
  private readonly reconRouteStore = inject(ReconRouteStore);

  readonly passengerData = signal<IPassengerSummary | null>(null);
  readonly extrasPerPassenger = signal<string[][]>([]);
  readonly passengerCount = signal<number>(1);

  readonly selectedConnectionOption = signal<IOnDemandConnectionOption | null>(
    null
  );

  readonly passengerTicketSelections = signal<
    {
      fahrgasttyp: string;
      useExistingTicket: boolean;
      existingTicketType: string | null;
    }[]
  >([]);

  readonly allPassengersPersonalData = signal<
    {
      salutation: string;
      firstName: string;
      lastName: string;
      birthDate: string;
    }[]
  >([]);

  readonly reservationExpiresAt = signal<string | null>(null);
  readonly reservationOperatorUrl = signal<string | null>(null);
  readonly checkoutTotalAmount = signal<number | null>(null);
  readonly checkoutTrip = signal<IJourneyTrip | null>(null);
  readonly checkoutOperatorMapping = signal<IOperatorMapping | null>(null);
  readonly checkoutProductGroups = signal<TariffProductGroup[]>([]);
  readonly checkoutSystemPayments = signal<ITariffSystemPayment[]>([]);
  readonly paymentCustomerData = signal<IOnDemandPaymentCustomerData | null>(
    null
  );
  readonly paymentCustomerReference = signal<string | null>(null);
  readonly paymentAsGuest = signal<boolean | null>(null);
  readonly pendingOrderId = signal<string | null>(null);
  readonly orderConfirmationPending = signal(false);

  readonly reservationBookingResponse = signal<IOnDemandBookingResponse | null>(
    null
  );

  readonly confirmBookingResponse = signal<IOnDemandBookingResponse | null>(
    null
  );

  constructor() {
    this.restorePaymentState();
  }

  selectConnectionOption(option: IOnDemandConnectionOption | null): void {
    const current = this.selectedConnectionOption();
    const hasChanged =
      current?.departureTimetabled !== option?.departureTimetabled ||
      current?.arrivalTimetabled !== option?.arrivalTimetabled;

    this.selectedConnectionOption.set(option);

    if (hasChanged) {
      this.reservationBookingResponse.set(null);
      this.confirmBookingResponse.set(null);
      this.reservationExpiresAt.set(null);
      this.paymentCustomerReference.set(null);
      this.paymentAsGuest.set(null);
      this.pendingOrderId.set(null);
      this.orderConfirmationPending.set(false);
    }
  }

  persistPaymentState(): void {
    if (typeof sessionStorage === 'undefined') return;

    sessionStorage.setItem(
      ON_DEMAND_PAYMENT_STATE_KEY,
      JSON.stringify({
        passengerData: this.passengerData(),
        extrasPerPassenger: this.extrasPerPassenger(),
        passengerCount: this.passengerCount(),
        selectedConnectionOption: this.selectedConnectionOption(),
        passengerTicketSelections: this.passengerTicketSelections(),
        allPassengersPersonalData: this.allPassengersPersonalData(),
        reservationExpiresAt: this.reservationExpiresAt(),
        reservationOperatorUrl: this.reservationOperatorUrl(),
        reservationBookingResponse: this.reservationBookingResponse(),
        confirmBookingResponse: this.confirmBookingResponse(),
        checkoutTotalAmount: this.checkoutTotalAmount(),
        checkoutTrip: this.checkoutTrip(),
        checkoutOperatorMapping: this.checkoutOperatorMapping(),
        checkoutProductGroups: this.checkoutProductGroups(),
        checkoutSystemPayments: this.checkoutSystemPayments(),
        paymentCustomerData: this.paymentCustomerData(),
        paymentCustomerReference: this.paymentCustomerReference(),
        paymentAsGuest: this.paymentAsGuest(),
        pendingOrderId: this.pendingOrderId(),
        orderConfirmationPending: this.orderConfirmationPending(),
      })
    );
  }

  private restorePaymentState(): void {
    if (typeof sessionStorage === 'undefined') return;

    const storedState = sessionStorage.getItem(ON_DEMAND_PAYMENT_STATE_KEY);
    if (!storedState) return;

    try {
      const state = JSON.parse(storedState);
      const expirationTimestamp = state.reservationExpiresAt
        ? new Date(state.reservationExpiresAt).getTime()
        : Number.NaN;
      if (
        Number.isFinite(expirationTimestamp) &&
        expirationTimestamp <= Date.now()
      ) {
        sessionStorage.removeItem(ON_DEMAND_PAYMENT_STATE_KEY);
        return;
      }

      this.passengerData.set(state.passengerData ?? null);
      this.extrasPerPassenger.set(state.extrasPerPassenger ?? []);
      this.passengerCount.set(state.passengerCount ?? 1);
      this.selectedConnectionOption.set(state.selectedConnectionOption ?? null);
      this.passengerTicketSelections.set(state.passengerTicketSelections ?? []);
      this.allPassengersPersonalData.set(state.allPassengersPersonalData ?? []);
      this.reservationExpiresAt.set(state.reservationExpiresAt ?? null);
      this.reservationOperatorUrl.set(state.reservationOperatorUrl ?? null);
      this.reservationBookingResponse.set(
        state.reservationBookingResponse ?? null
      );
      this.confirmBookingResponse.set(state.confirmBookingResponse ?? null);
      this.checkoutTotalAmount.set(state.checkoutTotalAmount ?? null);
      this.checkoutTrip.set(state.checkoutTrip ?? null);
      this.checkoutOperatorMapping.set(state.checkoutOperatorMapping ?? null);
      this.checkoutProductGroups.set(state.checkoutProductGroups ?? []);
      this.checkoutSystemPayments.set(state.checkoutSystemPayments ?? []);
      this.paymentCustomerData.set(state.paymentCustomerData ?? null);
      this.paymentCustomerReference.set(state.paymentCustomerReference ?? null);
      this.paymentAsGuest.set(state.paymentAsGuest ?? null);
      this.pendingOrderId.set(state.pendingOrderId ?? null);
      this.orderConfirmationPending.set(
        state.orderConfirmationPending ?? false
      );
    } catch {
      sessionStorage.removeItem(ON_DEMAND_PAYMENT_STATE_KEY);
    }
  }

  getReservationDeadlineLabel(): string | null {
    return formatIsoTime(this.reservationExpiresAt());
  }

  getReservationSessionTimeout(now: number = Date.now()): number {
    const expiration = this.reservationExpiresAt();
    if (!expiration) {
      return RESERVATION_DURATION_MINUTES * 60;
    }

    const expirationTimestamp = new Date(expiration).getTime();
    if (Number.isNaN(expirationTimestamp)) {
      return RESERVATION_DURATION_MINUTES * 60;
    }

    return Math.max(0, Math.floor((expirationTimestamp - now) / 1000));
  }

  getExtraLabel(type: string): string {
    return ON_DEMAND_EXTRA_LABELS[type] ?? type;
  }

  readonly confirmedDeparture = computed(() => {
    const confirmed =
      this.confirmBookingResponse()?.confirmedDeparture ??
      this.reservationBookingResponse()?.confirmedDeparture ??
      this.selectedConnectionOption()?.departureTimetabled;

    return formatIsoTime(confirmed);
  });

  readonly confirmedArrival = computed(() => {
    const confirmed =
      this.confirmBookingResponse()?.confirmedArrival ??
      this.reservationBookingResponse()?.confirmedArrival ??
      this.selectedConnectionOption()?.arrivalTimetabled;

    return formatIsoTime(confirmed);
  });

  readonly departureDeviationMinutes = computed(() => {
    const dep = this.selectedConnectionOption()?.departureTimetabled;
    const leg =
      this.availabilityStore.onDemandLeg() ??
      this.checkoutTrip()?.legs.find(
        journeyLeg =>
          journeyLeg.operator.id === this.checkoutOperatorMapping()?.operatorId
      );
    const tripDate =
      this.reconRouteStore.reconRoute()?.origin?.date ??
      this.checkoutTrip()?.origin?.date;

    if (!dep || !leg?.origin?.time || !tripDate) return null;

    return calculateTimeDeviationMinutes(dep, tripDate, leg.origin.time);
  });
}
